import { useEffect } from 'react';
import { seoConfig } from '../utils/seo-config';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: string;
  noIndex?: boolean;
}

/**
 * Hook pour gérer dynamiquement les méta-tags SEO
 * Optimisé pour Framerie par Dreambel Tech
 * 
 * @param seoProps Configuration SEO pour la page
 */
export const useSEO = (seoProps: SEOProps = {}) => {
  useEffect(() => {
    const {
      title = seoConfig.pages.home.title,
      description = seoConfig.pages.home.description,
      keywords = seoConfig.keywords,
      image = seoConfig.openGraph.images.default,
      url = seoConfig.siteUrl,
      type = 'website',
      noIndex = false
    } = seoProps;

    // Mise à jour du titre
    document.title = title;

    // Helper pour créer ou mettre à jour une méta-tag
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Méta-tags de base
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords.join(', '));
    updateMetaTag('author', `${seoConfig.author.name} - ${seoConfig.author.company}`);
    updateMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph - titres optimisés pour le partage social
    const socialTitle = title === seoConfig.pages.home.title ? 'Framerie' : title;
    updateMetaTag('og:title', socialTitle, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:image', `${seoConfig.siteUrl}${image}`, true);
    updateMetaTag('og:image:width', seoConfig.openGraph.images.width.toString(), true);
    updateMetaTag('og:image:height', seoConfig.openGraph.images.height.toString(), true);
    updateMetaTag('og:site_name', seoConfig.openGraph.siteName, true);
    updateMetaTag('og:locale', seoConfig.openGraph.locale, true);

    // Twitter Card - titres courts pour le partage
    updateMetaTag('twitter:card', seoConfig.twitter.cardType);
    updateMetaTag('twitter:title', socialTitle);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', `${seoConfig.siteUrl}${image}`);
    updateMetaTag('twitter:site', seoConfig.twitter.site);
    updateMetaTag('twitter:creator', seoConfig.twitter.creator);

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = url;

    // Schema.org JSON-LD pour la page
    const schemaData = {
      ...seoConfig.structuredData,
      name: seoConfig.siteName,
      description: description,
      url: url,
      author: {
        '@type': 'Person',
        name: seoConfig.author.name,
        url: seoConfig.author.website,
        sameAs: [
          seoConfig.author.instagram,
          seoConfig.author.linkedin
        ]
      },
      publisher: {
        '@type': 'Organization',
        name: seoConfig.author.company,
        url: seoConfig.author.website
      }
    };

    // Mise à jour du script JSON-LD
    let schemaScript = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(schemaData);

  }, [seoProps]);
};

/**
 * Hook spécialisé pour les pages de Framerie avec support multilingue
 * Inclut automatiquement les mots-clés spécifiques à l'application
 */
export const useFramerieSEO = (page: keyof typeof seoConfig.pages, additionalSEO: Partial<SEOProps> = {}) => {
  const pageConfig = seoConfig.pages[page];
  const userLanguage = typeof window !== 'undefined' ? 
    (localStorage.getItem('framerie-language') as 'en' | 'fr') || 'en' : 'en';
  
  // Utiliser la description selon la langue si disponible
  const getLocalizedContent = () => {
    if (userLanguage === 'fr' && 'descriptionFr' in pageConfig) {
      return {
        title: ('titleFr' in pageConfig && pageConfig.titleFr) || pageConfig.title,
        description: pageConfig.descriptionFr || pageConfig.description
      };
    }
    return {
      title: ('titleShort' in pageConfig && page === 'home') ? pageConfig.titleShort : pageConfig.title,
      description: pageConfig.description
    };
  };
  
  const localizedContent = getLocalizedContent();
  
  // Mots-clés spécifiques selon la page et la langue
  const getPageKeywords = () => {
    const pageKeywords: string[] = [];
    
    if (page === 'features') {
      pageKeywords.push(...(userLanguage === 'fr' ? 
        ['fonctionnalités capture', 'features screenshot'] : 
        ['screenshot features', 'capture features']));
    }
    if (page === 'pricing') {
      pageKeywords.push(...(userLanguage === 'fr' ? 
        ['prix capture écran', 'tarif application'] : 
        ['screenshot app pricing', 'capture tool price']));
    }
    if (page === 'download') {
      pageKeywords.push(...(userLanguage === 'fr' ? 
        ['télécharger capture', 'download screenshot app'] : 
        ['download screenshot', 'free screenshot app']));
    }
    if (page === 'demo') {
      pageKeywords.push(...(userLanguage === 'fr' ? 
        ['démo capture écran', 'demo screenshot'] : 
        ['screenshot demo', 'app demonstration']));
    }
    
    return pageKeywords;
  };
  
  const enhancedSEO: SEOProps = {
    title: localizedContent.title,
    description: localizedContent.description,
    url: `${seoConfig.siteUrl}${pageConfig.path}`,
    keywords: [
      ...seoConfig.keywords,
      ...getPageKeywords()
    ],
    ...additionalSEO
  };

  useSEO(enhancedSEO);
};

export default useSEO;
